import useWidth from '@components/Hooks/useWidth';
import { ScreenHeaderTagConfig } from '@components/v2/layout/ScreenHeader/screenHeader.types';
import { BreadcrumbItem } from '@components/v2/ui/Breadcrumb/breadcrumb.types';
import StatusIcon from '@components/v2/ui/StatusChip/components/StatusIcon';
import { useHistory } from 'react-router-dom';
import useGoBackUrl from '../../../../../hooks/useGoBackUrl';
import LangEnum from '../../../../../lang/LangEnum';
import PATHS from '../../../../../routes/routes.const';
import { isOfflineStatus } from '../../../../offline/domain/offlineOrder/offlineOrder.types';
import { useTranslation } from '../../../../translation/TranslationContext';
import { useOrderEditSectionsContext } from '../../../edit/sections/OrderEditSections.context';
import { ORDER_INTEGRATION_STATUS_CHIP_CONFIG } from '../../../list/components/orderIntegrationStatusChip.constants';
import { ORDER_OFFLINE_STATUS_CHIP_CONFIG } from '../../../list/components/orderOfflineStatusChip.constants';
import { ORDER_STATUS_CHIP_CONFIG } from '../../../list/components/orderStatusChip.constants';
import { Order } from '../../../order.types';
import OrderBreadcrumbCustomer from '../components/OrderBreadcrumbCustomer';
import { getOrderCustomerBreadcrumbLabel } from '../orderObjectPage.utils';

interface UseOrderObjectPageParams {
  order?: Order;
  isOfflineMode?: boolean;
}

const useOrderObjectPage = ({ order, isOfflineMode }: UseOrderObjectPageParams) => {
  const history = useHistory();
  const { translate } = useTranslation();
  const { smallWidth } = useWidth();
  const goBackUrl = useGoBackUrl(PATHS.main.orders.path);
  const { productsErrors, productsBusinessPolicyViolations } = useOrderEditSectionsContext();

  const goToOrderList = () => history.push(goBackUrl);

  const customerLabel = getOrderCustomerBreadcrumbLabel(order?.customer);

  const orderCodeLabel = order?.externalCode
    ? `${order.code} (${order.externalCode})`
    : order?.code;

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: LangEnum.GLOBAL_ORDERS, onClick: goToOrderList },
    ...(order
      ? [
          {
            label: `${translate(LangEnum.GLOBAL_ORDER)} ${orderCodeLabel}`,
            trailing: smallWidth ? undefined : <OrderBreadcrumbCustomer label={customerLabel} />,
          },
        ]
      : []),
  ];

  const tags: ScreenHeaderTagConfig[] = [];

  const statusConfig =
    order &&
    (isOfflineStatus(order.status)
      ? ORDER_OFFLINE_STATUS_CHIP_CONFIG[order.status]
      : ORDER_STATUS_CHIP_CONFIG[order.status]);
  if (statusConfig) {
    tags.push({
      label: statusConfig.label,
      icon: <StatusIcon icon={statusConfig.icon} color={statusConfig.color} size="small" />,
    });
  }

  const integrationConfig = order && ORDER_INTEGRATION_STATUS_CHIP_CONFIG[order.synchronized];
  if (!isOfflineMode && order?.hasBrandIntegrations && integrationConfig) {
    tags.push({
      label: integrationConfig.label,
      icon: (
        <StatusIcon icon={integrationConfig.icon} color={integrationConfig.color} size="small" />
      ),
    });
  }

  const hasProductsErrors =
    Object.keys(productsErrors).length > 0 || productsBusinessPolicyViolations.length > 0;

  return {
    breadcrumbItems,
    tags,
    productsErrors,
    productsBusinessPolicyViolations,
    hasProductsErrors,
  };
};

export default useOrderObjectPage;
